import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { concat, get, isEmpty, join, merge } from 'lodash';
import { CustomerContext, TenantContext } from '../contexts';
import { useReadCustomerAddressesApi, useReadFleetAddressesApi } from './index';
import { Environment } from '../services/index.js';
import { compact } from 'lodash/array.js';

function useCustomerAddresses() {
  const { customer } = useContext(CustomerContext);
  const { application } = useContext(TenantContext);

  const [customerAddresses, setCustomerAddresses] = useState(undefined);
  const [hasSavedAddress, setHasSavedAddress] = useState(false);
  const [fleetAddresses, setFleetAddresses] = useState(undefined);
  const [hasFleetAddress, setHasFleetAddress] = useState(false);
  const [customerReady, setCustomerReady] = useState(false);

  const {
    sendCallback: readCustomerAddressCallback,
    requestStatus: custAddrReqStatus
  } = useReadCustomerAddressesApi();
  const {
    sendCallback: readFleetAddressCallback,
    requestStatus: fleetAddrReqStatus
  } = useReadFleetAddressesApi();

  const fleetAcctsParam = useMemo(() => {
    if (!customer) return '';
    setCustomerReady(true);
    const fleetAccts = get(customer, 'attributes.fleetAccounts');
    return join(fleetAccts, ',');
  }, [customer]);

  const isFleetCustomerAndFleetDealer = useMemo(() => {
    return (
      auroraPayEnabled() &&
      application?.fleetPerformEnabled &&
      customer?.attributes?.['fleetPerformEnabled']
    );
  }, [application, customer]);

  const refetchAddresses = useCallback(
    (props = {}) => {
      const { fetchFleet = false, completedCallback = () => {} } = props;
      if (!customerReady) {
        return;
      }
      getAddressesWithPaging(
        setCustomerAddresses,
        setHasSavedAddress,
        readCustomerAddressCallback,
        {},
        completedCallback
      );
      if (isFleetCustomerAndFleetDealer && fetchFleet) {
        getAddressesWithPaging(
          setFleetAddresses,
          setHasFleetAddress,
          readFleetAddressCallback,
          { params: { fleetAccounts: fleetAcctsParam } },
          completedCallback
        );
      }
    },
    [
      readCustomerAddressCallback,
      readFleetAddressCallback,
      isFleetCustomerAndFleetDealer,
      customerReady,
      fleetAcctsParam
    ]
  );

  useEffect(() => {
    refetchAddresses({ fetchFleet: true });
  }, [refetchAddresses]);

  const filteredCustomerAddresses = useMemo(() => {
    // Remove customer's fleet addresses if not on a fleet dealer
    if (!customerAddresses) return [];
    if (isFleetCustomerAndFleetDealer) return customerAddresses;
    return customerAddresses.filter(address => !address.fleetAddress);
  }, [customerAddresses, isFleetCustomerAndFleetDealer]);

  const customizedFleetAddressIds = useMemo(() => {
    // Get all fleet location IDs of fleet addresses that the customer has customized
    const addr = Array.isArray(customerAddresses) ? customerAddresses : [];
    return compact(addr.map(addr => addr.fleetLocationId)).sort();
  }, [customerAddresses]);

  const customerFleetAddresses = useMemo(() => {
    if (!fleetAddresses || !isFleetCustomerAndFleetDealer) return [];
    const custFleetLocs = get(customer, 'attributes.fleetLocations', []).sort();
    return fleetAddresses
      .filter(
        fleetAddress =>
          !customizedFleetAddressIds.includes(fleetAddress.locationId)
      )
      .filter(fleetAddress =>
        locationValidForCustomer(custFleetLocs, fleetAddress)
      )
      .map(address => mapToCustomerAddress(address));
  }, [
    customer,
    fleetAddresses,
    customizedFleetAddressIds,
    isFleetCustomerAndFleetDealer
  ]);

  const combinedAddresses = useMemo(() => {
    return concat(filteredCustomerAddresses, customerFleetAddresses);
  }, [filteredCustomerAddresses, customerFleetAddresses]);

  return {
    customerAddresses: filteredCustomerAddresses,
    customerFleetAddresses,
    combinedAddresses,
    hasSavedAddress,
    hasFleetAddress,
    refetchAddresses,
    isFleetCustomerAndFleetDealer,
    custAddrReqStatus,
    fleetAddrReqStatus
  };
}

function auroraPayEnabled() {
  return Environment.get('AURORA_PAY_ENABLED', 'false') === 'true';
}

function locationValidForCustomer(custFleetLocs, fleetAddress) {
  const locationId = fleetAddress.locationId;
  for (let i = 0; i < custFleetLocs.length; i++) {
    const loc = custFleetLocs[i];
    if (loc < locationId) {
      continue;
    }
    if (loc === locationId) {
      return true;
    }
    if (loc > locationId) {
      return false;
    }
  }
  return false;
}

function mapToCustomerAddress(fltAdr) {
  return {
    id: fltAdr.locationId,
    name: fltAdr.name,
    addressLine1: fltAdr.addressLine1,
    addressLine2: fltAdr.addressLine2,
    addressLine3: fltAdr.addressLine3,
    city: fltAdr.city,
    stateProvinceRegion: fltAdr.state,
    postalCode: fltAdr.postal,
    country: fltAdr.country,
    phonePrimary: {
      phoneNumber: fltAdr.phone || ''
    },
    fleetAddress: true,
    fleetAccount: fltAdr.accountNumber,
    fleetLocationId: fltAdr.locationId,
    firstName: '',
    lastName: '',
    companyName: ''
  };
}

function getAddressesWithPaging(
  setter,
  setHasContent,
  readAddressesCallback,
  config = {},
  completedCallback = () => {}
) {
  (async () => {
    let stop = false;
    let hasContent = false;
    let allContent = [];
    let offset = 0;

    while (!stop) {
      const response = await readAddressesCallback(
        merge({}, { params: { offset: offset } }, config)
      ).catch(err => {
        stop = true;
        console.log(err);
      });
      if (isEmpty(response)) {
        stop = true;
      }
      if (!stop) {
        const { last, empty, content, size = [] } = response;
        if (!empty) {
          allContent.push(...content);
        }
        hasContent = hasContent || !empty;
        stop = !!empty || !!last;
        offset += size;
      }
    }
    setter(allContent);
    setHasContent(hasContent);
    completedCallback();
  })();
}

export default useCustomerAddresses;
